import { translateKey, useTranslation } from "../i18n";
import { createContext, useContext, useState, type ReactNode } from "react";
import { uploadedMediaUrl, type UploadDeliveryRegion } from "@nexra/contracts";
import { uploadInputMediaFile } from "./upload-input-media";
import { toast } from "../components/ui/toast";

const UploadContext = createContext<(files: readonly File[]) => Promise<string[]>>(
  async files => files.map(file => URL.createObjectURL(file)),
);

/** Session-local blob previews keyed by remote delivery URLs.
 * Form state only stores the remapped remote URL (for model submission), so the
 * UI needs a separate lookup — same split as Vibepaper localPreviewUrl. */
const previewByRemoteUrl = new Map<string, string>();

function createLocalPreviewUrl(file: Blob): string | undefined {
  try {
    return URL.createObjectURL(file);
  } catch {
    return undefined;
  }
}

function revokeLocalPreviewUrl(url: string | undefined): void {
  if (!url?.startsWith("blob:")) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* ignore */
  }
}

/** Register a browser-local preview for every owned delivery host variant. */
export function registerInputMediaPreview(remoteUrl: string, previewUrl: string | undefined): void {
  if (!previewUrl || !remoteUrl || remoteUrl.startsWith("blob:")) return;
  const keys = new Set<string>([
    remoteUrl,
    uploadedMediaUrl(remoteUrl, "cn"),
    uploadedMediaUrl(remoteUrl, "global"),
  ]);
  for (const key of keys) previewByRemoteUrl.set(key, previewUrl);
}

/** Prefer the local blob; otherwise show a browser-reachable owned host.
 * Form state may already point at a provider-facing regional hostname (e.g. tos.nexra-ai.com
 * for cn-beijing models) that does not serve the just-uploaded object to <img>. */
export function resolveInputMediaPreviewUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("blob:")) return url;
  const local = previewByRemoteUrl.get(url);
  if (local) return local;
  try {
    const parsed = new URL(url);
    if (
      ["tos.nexra-ai.com", "tos2.nexra-ai.com", "upload.nexra-ai.com"].includes(parsed.hostname)
      && parsed.pathname.startsWith("/user-assets/")
      && parsed.pathname !== "/user-assets/"
    ) {
      const publicHost = new URL(url);
      publicHost.hostname = "upload.nexra-ai.com";
      return publicHost.href;
    }
  } catch {
    /* keep original */
  }
  return url;
}

export function useInputMediaUpload() {
  return useContext(UploadContext);
}

export function InputMediaUploadProvider({ region, children }: { region: UploadDeliveryRegion; children: ReactNode }) {
  useTranslation();
  const [pending, setPending] = useState(0);
  const upload = async (files: readonly File[]) => {
    setPending(count => count + 1);
    const urls: string[] = [];
    try {
      for (const file of files) {
        const previewUrl = createLocalPreviewUrl(file);
        try {
          const storedUrl = await uploadInputMediaFile(file);
          const deliveryUrl = uploadedMediaUrl(storedUrl, region);
          registerInputMediaPreview(storedUrl, previewUrl);
          registerInputMediaPreview(deliveryUrl, previewUrl);
          urls.push(deliveryUrl);
        } catch (error) {
          revokeLocalPreviewUrl(previewUrl);
          throw error;
        }
      }
    } catch (error) {
      toast.error("素材上传失败，请重新选择文件重试", { description: error instanceof Error ? error.message : String(error) });
    } finally {
      setPending(count => count - 1);
    }
    return urls;
  };
  return <UploadContext.Provider value={upload}>
    <div className="contents" aria-busy={pending > 0}>
      {pending > 0 ? <span role="status" className="sr-only">{translateKey("workspace.uploaded")}</span> : null}
      {children}
    </div>
  </UploadContext.Provider>;
}
